import { useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import {
  Popover,
  Box,
  Typography,
  Button,
  Chip,
  Avatar,
  CircularProgress,
  Divider,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CustomTextInput from '../Forms/CustomTextInput';
import { priceTransform } from '../../helpers/formPrice';
import StockActionModal from './StockActionModal';
import { confirm } from '../MenuNew/ConfirmModal';
import { getMenusV2 } from '../../redux/actions/Menu/menuV2';

const QuickEditPopover = ({
  open,
  anchorEl,
  onClose,
  item,
  onSave,
  onStockAction,
}) => {
  const dispatch = useDispatch();
  const [selectedAction, setSelectedAction] = useState(null); // 'remove', 'add', or 'adjust'
  const [stockModalOpen, setStockModalOpen] = useState(false);
  const [stockActionLoading, setStockActionLoading] = useState(false);
  const [lastItemId, setLastItemId] = useState(null);
  const [lastItemPrice, setLastItemPrice] = useState(null);
  const [lastItemCost, setLastItemCost] = useState(null);
  const [loading, setLoading] = useState(false);
  console.log(item);

  const methods = useForm({
    defaultValues: {
      price: item?.price || '0',
      cost: item?.inventoryInfo?.unitPrice || '0',
    },
  });
  const { setValue, formState } = methods;
  const isDirty = formState.isDirty;

  useEffect(() => {
    if (item) {
      const currentItemId = item._id || item.id;
      const currentPrice = item.price;
      const currentCost = item.inventoryInfo?.unitPrice;
      const isNewItem = lastItemId !== currentItemId;
      const priceChanged = lastItemPrice !== currentPrice;
      const costChanged = lastItemCost !== currentCost;

      // Only reset form if:
      // 1. It's a different item, OR
      // 2. Price or Cost changed from backend (not from user input in form)
      // Don't reset if only quantity changed
      if (
        isNewItem ||
        (priceChanged && !isDirty) ||
        (costChanged && !isDirty)
      ) {
        methods.reset({
          price: item.price || '0',
          cost: item.inventoryInfo?.unitPrice || '0',
        });
        setLastItemId(currentItemId);
        setLastItemPrice(currentPrice);
        setLastItemCost(currentCost);
      } else if (!isNewItem && !priceChanged && !costChanged) {
        // Only quantity changed - update references but don't reset form
        setLastItemId(currentItemId);
        setLastItemPrice(currentPrice);
        setLastItemCost(currentCost);
      }
    }
  }, [item, methods, lastItemId, lastItemPrice, lastItemCost, isDirty]);

  useEffect(() => {
    // Reset action when popover closes
    if (!open) {
      setSelectedAction(null);
      setStockModalOpen(false);
      // Reset form to clear dirty state when popover closes
      if (item) {
        methods.reset({
          price: item.price || '0',
          cost: item.inventoryInfo?.unitPrice || '0',
        });
      }
      setLastItemId(null);
      setLastItemPrice(null);
      setLastItemCost(null);
    }
  }, [open, item, methods]);

  const onChangePrice = (e) => {
    const { name, value } = e.target;
    const transformedValue = priceTransform(value);
    setValue(name, transformedValue, { shouldDirty: true });
  };

  const onChangeCost = (e) => {
    const { name, value } = e.target;
    const transformedValue = priceTransform(value);
    setValue(name, transformedValue, { shouldDirty: true });
  };

  const handleSave = async (data, shouldClose = true) => {
    // Save price and cost (unitPrice) fields at top level
    setLoading(true);
    const priceValue =
      data.price === '' || data.price === null || data.price === undefined
        ? 0
        : parseFloat(data.price);
    const costValue =
      data.cost === '' || data.cost === null || data.cost === undefined
        ? undefined
        : parseFloat(data.cost);
    await onSave({
      ...item,
      price: isNaN(priceValue) ? 0 : priceValue,
      unitPrice:
        costValue !== undefined && !isNaN(costValue) ? costValue : undefined,
    });
    // Refresh menu data after successful save
    await dispatch(getMenusV2(false));
    // Reset dirty state after save
    methods.reset(data);
    setLoading(false);
    if (shouldClose) {
      onClose();
    }
  };

  const handleActionClick = (action) => {
    setSelectedAction(action);
    setStockModalOpen(true);
  };

  const handleCloseStockModal = () => {
    setStockModalOpen(false);
    setSelectedAction(null);
  };

  const handleSaveStockAction = async (quantity) => {
    if (onStockAction && item && selectedAction) {
      try {
        setStockActionLoading(true);
        // Stock action will update item quantity
        // Form values will be preserved because price/cost haven't changed
        await onStockAction(selectedAction, item, quantity);
        // Refresh menu data after successful stock action
        await dispatch(getMenusV2(false));

        // Close the stock modal only after successful save
        handleCloseStockModal();
      } catch (error) {
        console.error('Error saving stock action:', error);
        // Don't close modal on error, let user try again
      } finally {
        setStockActionLoading(false);
      }
    }
  };

  const getStatusColor = (item) => {
    const inventory = item?.inventoryInfo || {};
    const stock = inventory.stock || item?.stock || 0;
    const minStock = inventory.minStockLevel || item?.minStockLevel || 0;
    const _maxStock =
      inventory.maxStockLevel || item?.maxStockLevel || Infinity;

    if (stock <= 0) {
      return {
        backgroundColor: '#E2150814',
        textColor: '#E21508',
      };
    } else if (stock <= minStock) {
      return {
        backgroundColor: '#FF8A0014',
        textColor: '#FF8A00',
      };
    }
    return {
      backgroundColor: '#00984F14',
      textColor: '#00984F',
    };
  };

  const getStatusText = (item) => {
    const inventory = item?.inventoryInfo || {};
    const stock = inventory.stock || item?.stock || 0;
    const minStock = inventory.minStockLevel || item?.minStockLevel || 0;
    const _maxStock =
      inventory.maxStockLevel || item?.maxStockLevel || Infinity;

    if (stock <= 0) {
      return 'Out of Stock';
    } else if (stock <= minStock) {
      return 'Low Stock';
    }
    return 'In Stock';
  };

  return (
    <>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={async () => {
          if (isDirty) {
            const result = await confirm({
              title: 'confirm_modal.title',
              message:
                'You have unsaved changes. Are you sure you want to close?',
              cancelText: 'confirm_modal.cancel',
              confirmText: 'confirm_modal.save_and_leave',
              discardText: 'confirm_modal.discard',
              hideCloseIcon: false,
              hideDiscardBtn: false,
              background: '#1976d2',
            });
            if (result === true) {
              // Save and leave - save first then close
              const formData = methods.getValues();
              await handleSave(formData);
            } else if (result === 'discard') {
              // Discard changes and close
              onClose();
            }
            // If result is false, user cancelled - do nothing
          } else {
            onClose();
          }
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              width: 440,
              p: 3,
              position: 'relative',
            },
          },
        }}
      >
        <IconButton
          onClick={async () => {
            if (isDirty) {
              const result = await confirm({
                title: 'confirm_modal.title',
                message:
                  'You have unsaved changes. Are you sure you want to close?',
                cancelText: 'confirm_modal.cancel',
                confirmText: 'confirm_modal.save_and_leave',
                discardText: 'confirm_modal.discard',
                hideCloseIcon: false,
                hideDiscardBtn: false,
                background: '#1976d2',
              });
              if (result === true) {
                // Save and leave - save first then close
                const formData = methods.getValues();
                await handleSave(formData);
              } else if (result === 'discard') {
                // Discard changes and close
                onClose();
              }
              // If result is false, user cancelled - do nothing
            } else {
              onClose();
            }
          }}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <FormProvider {...methods}>
          <Box component="form" onSubmit={methods.handleSubmit(handleSave)}>
            {/* Item Info Section */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
              }}
            >
              <Avatar
                src={item?.image?.url}
                sx={{ width: 108, height: 108, mr: 2, fontSize: '26px' }}
                variant="rounded"
              >
                {`${item?.name?.charAt(0)}${item?.name?.charAt(1)}`}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Chip
                  label={getStatusText(item)}
                  size="small"
                  variant="filled"
                  sx={{
                    mb: 1,
                    backgroundColor: getStatusColor(item).backgroundColor,
                    color: getStatusColor(item).textColor,
                    '& .MuiChip-label': {
                      color: getStatusColor(item).textColor,
                    },
                  }}
                />
                <Tooltip title={item?.name || ''} arrow>
                  <Typography
                    component="div"
                    sx={{
                      fontWeight: '500',
                      fontSize: '18px',
                      mb: 0.5,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      width: '205px',
                      wordBreak: 'break-all',
                      display: 'block',
                    }}
                  >
                    {item?.name}
                  </Typography>
                </Tooltip>
                <Tooltip title={item?.category?.name || ''} arrow>
                  <Typography
                    component="div"
                    sx={{
                      fontWeight: '400',
                      fontSize: '16px',
                      color: '#929398',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      width: '205px',
                      wordBreak: 'break-all',
                      display: 'block',
                    }}
                  >
                    {item?.category?.name || ''}
                  </Typography>
                </Tooltip>
              </Box>
            </Box>

            {/* SKU, Barcode, Unit of Measure Section */}
            <Box sx={{ mt: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  // justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '16px',
                    color: '#929398',
                    width: 132,
                  }}
                >
                  SKU:
                </Typography>
                <Typography
                  sx={{
                    fontSize: '16px',
                    color: '#0F1214',
                    fontWeight: 400,
                  }}
                >
                  {item?.inventoryInfo?.sku || '-'}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '16px',
                    color: '#929398',
                    width: 132,
                  }}
                >
                  Barcodes:
                </Typography>
                <Box
                  sx={{
                    width: '205px',
                    overflow: 'auto',
                    display: 'flex',
                    gap: 0.5,
                    flexWrap: 'wrap',
                  }}
                >
                  {item?.barcodeNumbers?.length
                    ? item.barcodeNumbers.map((barcode) => (
                        <Chip key={barcode} label={barcode} size="small" />
                      ))
                    : '-'}
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '16px',
                    color: '#929398',
                    width: 132,
                  }}
                >
                  Unit of Measure:
                </Typography>
                <Typography
                  sx={{
                    fontSize: '16px',
                    color: '#0F1214',
                    fontWeight: 400,
                  }}
                >
                  {item?.inventoryInfo?.unit || '-'}
                </Typography>
              </Box>
            </Box>

            <Divider
              sx={{ marginLeft: '-32px', marginRight: '-32px', my: 3 }}
            />

            {/* Editable Fields */}
            <Box
              sx={{
                flexDirection: 'column',
                gap: 2,
                display: 'flex',
              }}
            >
              {/* Price, Cost, and Save Button in one row */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                {/* Price Field */}
                <Box sx={{ flex: 1 }}>
                  <CustomTextInput
                    name="price"
                    label="Price *"
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                    }}
                    inputProps={{ type: 'number', step: 'any' }}
                    onChange={onChangePrice}
                    sx={{
                      width: '100%',
                    }}
                    disabled={loading}
                  />
                </Box>

                {/* Cost Field */}
                <Box sx={{ flex: 1 }}>
                  <CustomTextInput
                    name="cost"
                    label="Cost"
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                    }}
                    inputProps={{ type: 'number', step: 'any' }}
                    onChange={onChangeCost}
                    sx={{
                      width: '100%',
                    }}
                    disabled={loading}
                  />
                </Box>

                {/* Save Button */}
                <Button
                  variant="contained"
                  type="submit"
                  disabled={loading || !isDirty}
                  size="medium"
                  sx={{
                    height: 40,
                    minWidth: 100,
                  }}
                  startIcon={loading ? <CircularProgress size={16} /> : null}
                >
                  SAVE
                </Button>
              </Box>

              {/* Stock Management Section */}
              <Box
                sx={{
                  mb: 2,
                }}
              >
                {/* Quantity and Action Buttons in one row */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {/* Quantity Input - always readonly */}
                  <TextField
                    label="Quantity"
                    type="number"
                    value={item?.inventoryInfo?.stock || item?.stock || 0}
                    inputProps={{ min: 0, readOnly: true }}
                    size="small"
                    focused={false}
                    disabled={true}
                    sx={{
                      flex: 1,
                      '& .MuiInputBase-root': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  />

                  {/* Action Buttons */}
                  <Button
                    variant={'outlined'}
                    color="error"
                    onClick={() => handleActionClick('remove')}
                    size="small"
                    sx={{
                      minWidth: 80,
                      height: '40px', // Match TextField small size height
                    }}
                    disabled={loading}
                  >
                    REMOVE
                  </Button>
                  <Button
                    variant={'outlined'}
                    onClick={() => handleActionClick('add')}
                    size="small"
                    sx={{
                      minWidth: 80,
                      height: '40px', // Match TextField small size height
                    }}
                    disabled={loading}
                  >
                    ADD
                  </Button>
                  <Button
                    variant={'outlined'}
                    onClick={() => handleActionClick('adjust')}
                    size="small"
                    sx={{
                      minWidth: 80,
                      height: '40px', // Match TextField small size height
                    }}
                    disabled={loading}
                  >
                    ADJUST
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </FormProvider>
      </Popover>

      {/* Stock Action Modal */}
      <StockActionModal
        open={stockModalOpen}
        onClose={handleCloseStockModal}
        action={selectedAction}
        onSave={handleSaveStockAction}
        loading={stockActionLoading}
      />
    </>
  );
};

export default QuickEditPopover;
